import { createTheme } from '@mui/material/styles';

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a1a1a',
      light: '#333333',
      dark: '#000000',
      contrastText: '#faf8f5',
    },
    secondary: {
      main: '#c9a96e',
      light: '#d4b87e',
      dark: '#b8935e',
      contrastText: '#1a1a1a',
    },
    accent: {
      main: '#3498db',
      light: '#5dade2',
      dark: '#2980b9',
      contrastText: '#ffffff',
    },
    background: {
      default: '#faf8f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#6b6560',
    },
    success: {
      main: '#27ae60',
      light: '#2ecc71',
      dark: '#229954',
    },
    warning: {
      main: '#f39c12',
      light: '#f1c40f',
      dark: '#e67e22',
    },
    error: {
      main: '#e74c3c',
      light: '#ec7063',
      dark: '#c0392b',
    },
  },
  typography: {
    fontFamily: '"DM Sans", system-ui, sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 300,
      fontStyle: 'italic',
      fontSize: '3rem',
      lineHeight: 1.1,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 300,
      fontSize: '2.4rem',
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 500,
      fontSize: '1.6rem',
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 500,
      fontSize: '1.3rem',
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 600,
      fontSize: '1.15rem',
      lineHeight: 1.35,
      letterSpacing: 0.1,
    },
    subtitle1: {
      fontFamily: 'inherit',
      fontWeight: 500,
      fontSize: '0.95rem',
      lineHeight: 1.45,
    },
    subtitle2: {
      fontFamily: 'inherit',
      fontWeight: 500,
      fontSize: '0.85rem',
      lineHeight: 1.4,
      textTransform: 'none',
    },
    body1: {
      fontFamily: 'inherit',
      fontSize: '0.95rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontFamily: 'inherit',
      fontSize: '0.85rem',
      lineHeight: 1.55,
      fontWeight: 400,
    },
    button: {
      fontFamily: 'inherit',
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      letterSpacing: 0.3,
    },
    caption: {
      fontFamily: 'inherit',
      fontSize: '0.72rem',
      lineHeight: 1.3,
      fontWeight: 500,
      letterSpacing: 0.4,
    },
    overline: {
      fontFamily: 'inherit',
      fontSize: '0.6rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 4px 8px rgba(0,0,0,0.12)',
    '0 6px 12px rgba(0,0,0,0.15)',
    '0 8px 16px rgba(0,0,0,0.18)',
    '0 10px 20px rgba(0,0,0,0.2)',
    '0 12px 24px rgba(0,0,0,0.22)',
    '0 14px 28px rgba(0,0,0,0.25)',
    '0 16px 32px rgba(0,0,0,0.28)',
    '0 18px 36px rgba(0,0,0,0.3)',
    '0 20px 40px rgba(0,0,0,0.32)',
    '0 22px 44px rgba(0,0,0,0.35)',
    '0 24px 48px rgba(0,0,0,0.38)',
    '0 26px 52px rgba(0,0,0,0.4)',
    '0 28px 56px rgba(0,0,0,0.42)',
    '0 30px 60px rgba(0,0,0,0.45)',
    '0 32px 64px rgba(0,0,0,0.48)',
    '0 34px 68px rgba(0,0,0,0.5)',
    '0 36px 72px rgba(0,0,0,0.52)',
    '0 38px 76px rgba(0,0,0,0.55)',
    '0 40px 80px rgba(0,0,0,0.58)',
    '0 42px 84px rgba(0,0,0,0.6)',
    '0 44px 88px rgba(0,0,0,0.62)',
    '0 46px 92px rgba(0,0,0,0.65)',
    '0 48px 96px rgba(0,0,0,0.68)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.3s ease',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#c9a96e',
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#375a7f',
      light: '#4a6fa5',
      dark: '#2c4a6b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#e67e22',
      light: '#f39c12',
      dark: '#d35400',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#3498db',
      light: '#5dade2',
      dark: '#2980b9',
      contrastText: '#ffffff',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#f8f9fa',
      secondary: '#bbbbbb',
    },
    success: {
      main: '#27ae60',
      light: '#2ecc71',
      dark: '#229954',
    },
    warning: {
      main: '#f39c12',
      light: '#f1c40f',
      dark: '#e67e22',
    },
    error: {
      main: '#e74c3c',
      light: '#ec7063',
      dark: '#c0392b',
    },
  },
  typography: {
    fontFamily: '"DM Sans", system-ui, sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 300,
      fontStyle: 'italic',
      fontSize: '3rem',
      lineHeight: 1.1,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 300,
      fontSize: '2.4rem',
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 500,
      fontSize: '1.6rem',
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 500,
      fontSize: '1.3rem',
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 600,
      fontSize: '1.15rem',
      lineHeight: 1.35,
      letterSpacing: 0.1,
    },
    subtitle1: {
      fontFamily: 'inherit',
      fontWeight: 500,
      fontSize: '0.95rem',
      lineHeight: 1.45,
    },
    subtitle2: {
      fontFamily: 'inherit',
      fontWeight: 500,
      fontSize: '0.85rem',
      lineHeight: 1.4,
    },
    body1: {
      fontFamily: 'inherit',
      fontSize: '0.95rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontFamily: 'inherit',
      fontSize: '0.85rem',
      lineHeight: 1.55,
      fontWeight: 400,
    },
    button: {
      fontFamily: 'inherit',
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      letterSpacing: 0.3,
    },
    caption: {
      fontFamily: 'inherit',
      fontSize: '0.72rem',
      lineHeight: 1.3,
      fontWeight: 500,
      letterSpacing: 0.4,
    },
    overline: {
      fontFamily: 'inherit',
      fontSize: '0.6rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});
